/**
 * 解析 AI.chat 流式响应（SSE 或纯文本），提取正文增量。
 * 浏览器侧优先用 XHR onprogress，避免 fetch ReadableStream 被代理缓冲后一次性返回。
 */

function extractStreamDelta(data) {
  if (data == null) return '';
  if (typeof data === 'string') return data;
  if (typeof data.text === 'string') return data.text;
  if (typeof data.content === 'string') return data.content;
  if (typeof data.value === 'string') return data.value;
  if (typeof data.delta === 'string') return data.delta;
  if (data.delta && typeof data.delta.content === 'string') return data.delta.content;
  if (data.choices && data.choices[0]) {
    const choice = data.choices[0];
    if (choice.delta && typeof choice.delta.content === 'string') return choice.delta.content;
    if (choice.message && typeof choice.message.content === 'string') return choice.message.content;
  }
  if (data.data) {
    if (typeof data.data === 'string') return data.data;
    if (typeof data.data.content === 'string') return data.data.content;
    if (typeof data.data.text === 'string') return data.data.text;
  }
  if (data.message && typeof data.message.content === 'string') return data.message.content;
  return '';
}

function looksLikeSse(text) {
  return /(^|\n)\s*data:\s*/.test(text);
}

function normalizeLine(line) {
  return String(line || '').replace(/\r$/, '');
}

function parseSseLine(line) {
  const normalized = normalizeLine(line).trim();
  if (!normalized.startsWith('data:')) return '';
  const raw = normalized.replace(/^data:\s*/, '').trim();
  if (!raw || raw === '[DONE]') return '';
  try {
    return extractStreamDelta(JSON.parse(raw));
  } catch (e) {
    return raw;
  }
}

function parseTrailingBuffer(buffer) {
  const trimmed = String(buffer || '').trim();
  if (!trimmed) return '';
  if (trimmed.startsWith('data:')) return parseSseLine(trimmed);
  try {
    return extractStreamDelta(JSON.parse(trimmed)) || trimmed;
  } catch (e) {
    return trimmed;
  }
}

function createStreamParser(contentType) {
  let buffer = '';
  let fullContent = '';
  let mode = 'auto';
  const ct = String(contentType || '').toLowerCase();
  if (ct.indexOf('text/plain') !== -1) {
    mode = 'plain';
  } else if (ct.indexOf('text/event-stream') !== -1 || ct.indexOf('stream') !== -1) {
    mode = 'sse';
  }

  const appendText = (delta) => {
    if (!delta) return fullContent;
    fullContent += delta;
    return fullContent;
  };

  const processSseLines = (lines) => {
    for (let i = 0; i < lines.length; i += 1) {
      appendText(parseSseLine(lines[i]));
    }
    return fullContent;
  };

  const push = (chunk) => {
    if (!chunk) return fullContent;

    if (mode === 'plain') {
      return appendText(chunk);
    }

    buffer += chunk;
    if (mode === 'auto') {
      if (looksLikeSse(buffer)) {
        mode = 'sse';
      } else if (buffer.indexOf('\n') !== -1) {
        mode = 'plain';
        appendText(buffer);
        buffer = '';
        return fullContent;
      }
      return fullContent;
    }

    const lines = buffer.split('\n');
    buffer = lines.pop() || '';
    return processSseLines(lines);
  };

  const finish = (tailDecoderText) => {
    if (tailDecoderText) {
      if (mode === 'plain') {
        appendText(tailDecoderText);
      } else {
        buffer += tailDecoderText;
      }
    }

    if (mode === 'sse' && buffer) {
      const lines = buffer.split('\n');
      const rest = lines.pop() || '';
      processSseLines(lines);
      appendText(parseTrailingBuffer(rest));
      buffer = '';
    } else if ((mode === 'plain' || mode === 'auto') && buffer) {
      appendText(buffer);
      buffer = '';
    }

    return fullContent;
  };

  return {
    push,
    finish,
    getFull() {
      return fullContent;
    },
    setContentType(contentType) {
      const next = String(contentType || '').toLowerCase();
      if (next.indexOf('text/plain') !== -1) {
        mode = 'plain';
      } else if (next.indexOf('text/event-stream') !== -1 || next.indexOf('stream') !== -1) {
        mode = 'sse';
      }
    },
  };
}

/**
 * XHR 流式请求（推荐，浏览器可多次 onprogress 增量更新 UI）
 * @param {{ url: string, headers?: Record<string, string>, body: object }} request
 * @param {(fullText: string) => void} onUpdate
 * @returns {Promise<string>}
 */
export function streamAiChatXhr(request, onUpdate) {
  const { url, headers, body } = request || {};
  if (!url) {
    return Promise.reject(new Error('缺少请求地址'));
  }

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url, true);
    Object.entries(headers || {}).forEach(([key, value]) => {
      if (value != null && value !== '') {
        xhr.setRequestHeader(key, value);
      }
    });

    const parser = createStreamParser('');
    let lastLen = 0;
    let settled = false;

    const pump = () => {
      const text = xhr.responseText || '';
      if (text.length <= lastLen) return;
      const chunk = text.slice(lastLen);
      lastLen = text.length;
      const contentType = xhr.getResponseHeader('Content-Type') || '';
      if (contentType) parser.setContentType(contentType);
      onUpdate(parser.push(chunk));
    };

    const settleOk = () => {
      if (settled) return;
      settled = true;
      const contentType = xhr.getResponseHeader('Content-Type') || '';
      if (contentType) parser.setContentType(contentType);
      pump();
      const full = parser.finish('');
      onUpdate(full);
      resolve(full);
    };

    const settleErr = (message) => {
      if (settled) return;
      settled = true;
      reject(new Error(message || '请求失败'));
    };

    xhr.onprogress = pump;
    xhr.onreadystatechange = () => {
      if (xhr.readyState >= 3) pump();
      if (xhr.readyState !== 4) return;
      if (xhr.status >= 200 && xhr.status < 300) {
        settleOk();
        return;
      }
      let msg = xhr.statusText || `请求失败 ${xhr.status}`;
      try {
        const raw = xhr.responseText || '';
        if (raw) {
          try {
            const data = JSON.parse(raw);
            msg = data.message || data.error || data.msg || raw;
          } catch (e) {
            msg = raw.slice(0, 400);
          }
        }
      } catch (e) { /* ignore */ }
      settleErr(msg);
    };
    xhr.onerror = () => settleErr('网络错误');
    xhr.onabort = () => settleErr('请求已取消');
    xhr.send(JSON.stringify(body || {}));
  });
}

/**
 * fetch ReadableStream 读取（部分环境会被缓冲，保留作兼容）
 */
export async function readAiChatStream(stream, onUpdate) {
  const reader = stream && stream.reader ? stream.reader : stream;
  const contentType = String((stream && stream.contentType) || '').toLowerCase();
  if (!reader || typeof reader.read !== 'function') {
    throw new Error('未能建立流式连接');
  }

  const decoder = new TextDecoder();
  const parser = createStreamParser(contentType);

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    if (!value) continue;
    const chunk = value instanceof Uint8Array
      ? decoder.decode(value, { stream: true })
      : String(value);
    onUpdate(parser.push(chunk));
  }

  const full = parser.finish(decoder.decode());
  onUpdate(full);
  return full;
}
